// src/hooks/useAlertSystem.js
// ─────────────────────────────────────────────────────────────────────────────
// --- UPDATE (Alert System AI) — NEW: all data, live updates and actions of the Alert System
// page in one hook, so the page only lays out components.
//
//   Data        alerts (paged, filtered), rules (paged, filtered), stats (one request),
//               FlowSense AI intelligence snapshot, AI rule suggestions.
//   Search      typed text is debounced (400 ms) before it reaches the API — the old page
//               fetched on every keystroke AND again after the debounce.
//   Live        WebSocket: new_sensor_alert, critical_anomaly, sensor_alert_updated,
//               sensor_alert_deleted, work_order_status_updated, alert_ai_snapshot,
//               critical_alert_escalated. Bursts are coalesced into one refresh per 1.2 s.
//               New critical alerts: toast + optional sound, once per alert (the old page beeped
//               on every refresh while any critical alert existed).
//   Stale data  every fetch carries a sequence number; late responses from an older filter are
//               dropped instead of overwriting newer results.
//   Selection   lives here (the table's own selection never reached the bulk buttons before).
//   Actions     acknowledge / resolve one or many (one bulk request), notes, rule CRUD,
//               enable / disable, apply FlowSense AI rule tuning, CSV / JSON export.
// ─────────────────────────────────────────────────────────────────────────────
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as alertRuleService from '../services/alertRuleService';
import * as activeSensorAlertService from '../services/activeSensorAlertService';
import webSocketService from '../services/websocketService';
import {
    downloadFile, playAlertTone, readLocal, toCsv, writeLocal, wsPayload, statusLabel,
} from '../components/alerts/alertUtils';

const SEARCH_DEBOUNCE_MS = 400;
const LIVE_COALESCE_MS = 1200;
const EMPTY_PAGINATION = { currentPage: 1, totalPages: 1, limit: 10, totalItems: 0 };

function useDebounced(value, ms) {
    const [v, setV] = useState(value);
    useEffect(() => {
        const t = setTimeout(() => setV(value), ms);
        return () => clearTimeout(t);
    }, [value, ms]);
    return v;
}

export default function useAlertSystem({ toast }) {
    // ── alerts ───────────────────────────────────────────────────────────────
    const [alerts, setAlerts] = useState([]);
    const [alertPagination, setAlertPagination] = useState(EMPTY_PAGINATION);
    const [alertFilters, setAlertFilters] = useState({ search: '', status: '', severity: '', page: 1, limit: 10, sortBy: 'triggered_at', sortOrder: 'DESC' });
    const [alertsLoading, setAlertsLoading] = useState(false);
    const [alertsError, setAlertsError] = useState(null);
    const debouncedAlertSearch = useDebounced(alertFilters.search, SEARCH_DEBOUNCE_MS);

    // ── rules ────────────────────────────────────────────────────────────────
    const [rules, setRules] = useState([]);
    const [rulePagination, setRulePagination] = useState(EMPTY_PAGINATION);
    const [ruleFilters, setRuleFilters] = useState({ search: '', is_enabled: '', severity: '', page: 1, limit: 10, sortBy: 'name', sortOrder: 'ASC' });
    const [rulesLoading, setRulesLoading] = useState(false);
    const debouncedRuleSearch = useDebounced(ruleFilters.search, SEARCH_DEBOUNCE_MS);

    // ── stats / AI ───────────────────────────────────────────────────────────
    const [stats, setStats] = useState(() => readLocal('flowSense_alertStats_v2', null));
    const [intel, setIntel] = useState(null);
    const [intelLoading, setIntelLoading] = useState(false);
    const [intelError, setIntelError] = useState(null);
    const [suggestions, setSuggestions] = useState([]);
    const [suggestionsLoading, setSuggestionsLoading] = useState(true);

    // ── ui state ─────────────────────────────────────────────────────────────
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const [highlightIds, setHighlightIds] = useState(() => new Set());
    const [soundEnabled, setSoundEnabledState] = useState(() => readLocal('flowSense_alertSound', false));
    const [lastUpdated, setLastUpdated] = useState(null);
    const [isWorking, setIsWorking] = useState(false);

    const seq = useRef({ alerts: 0, rules: 0, intel: 0 });
    const announced = useRef(new Set());
    const liveTimer = useRef(null);
    const mounted = useRef(true);
    useEffect(() => () => { mounted.current = false; clearTimeout(liveTimer.current); }, []);

    const alertQuery = useMemo(() => ({ ...alertFilters, search: debouncedAlertSearch }), [alertFilters, debouncedAlertSearch]);
    const ruleQuery = useMemo(() => ({ ...ruleFilters, search: debouncedRuleSearch }), [ruleFilters, debouncedRuleSearch]);
    const isSearchingAlerts = alertFilters.search !== debouncedAlertSearch;
    const isSearchingRules = ruleFilters.search !== debouncedRuleSearch;

    const notifyError = useCallback((title, error) => {
        toast?.({ title, description: error?.message || String(error || ''), status: 'error', duration: 5000, isClosable: true });
    }, [toast]);

    // ── fetchers ─────────────────────────────────────────────────────────────
    const fetchAlerts = useCallback(async ({ silent = false } = {}) => {
        const my = ++seq.current.alerts;
        if (!silent) setAlertsLoading(true);
        try {
            const res = await activeSensorAlertService.fetchActiveAlerts(alertQuery);
            if (!mounted.current || my !== seq.current.alerts) return;
            setAlerts(res?.data || []);
            setAlertPagination(res?.pagination?.totalPages ? res.pagination : { ...EMPTY_PAGINATION, limit: alertQuery.limit, totalItems: (res?.data || []).length });
            setAlertsError(null);
            setLastUpdated(new Date());
        } catch (e) {
            if (my !== seq.current.alerts) return;
            setAlertsError(e?.message || 'Could not load alerts.');
            if (!silent) notifyError('Error fetching alerts', e);
        } finally {
            if (mounted.current && my === seq.current.alerts) setAlertsLoading(false);
        }
    }, [alertQuery, notifyError]);

    const fetchRules = useCallback(async ({ silent = false } = {}) => {
        const my = ++seq.current.rules;
        if (!silent) setRulesLoading(true);
        try {
            const params = { ...ruleQuery };
            if (params.is_enabled === '') delete params.is_enabled;
            const res = await alertRuleService.fetchAlertRules(params);
            if (!mounted.current || my !== seq.current.rules) return;
            setRules(res?.data || []);
            setRulePagination(res?.pagination?.totalPages ? res.pagination : { ...EMPTY_PAGINATION, limit: ruleQuery.limit, totalItems: (res?.data || []).length });
        } catch (e) {
            if (my === seq.current.rules && !silent) notifyError('Error fetching alert rules', e);
        } finally {
            if (mounted.current && my === seq.current.rules) setRulesLoading(false);
        }
    }, [ruleQuery, notifyError]);

    const fetchStats = useCallback(async () => {
        try {
            const s = await activeSensorAlertService.fetchAlertStats();
            if (!mounted.current || !s?.alerts) return;
            setStats(s);
            writeLocal('flowSense_alertStats_v2', s);
        } catch (_) { /* keep the cached numbers; the KPI strip says when they were updated */ }
    }, []);

    const fetchIntel = useCallback(async ({ refresh = false, silent = false } = {}) => {
        const my = ++seq.current.intel;
        if (!silent) setIntelLoading(true);
        try {
            const data = await activeSensorAlertService.fetchAlertIntelligence({ refresh });
            if (!mounted.current || my !== seq.current.intel) return;
            setIntel(data);
            setIntelError(null);
            if (data?.stats?.alerts) { setStats(data.stats); writeLocal('flowSense_alertStats_v2', data.stats); }
        } catch (e) {
            if (my === seq.current.intel) setIntelError(e?.message || 'FlowSense AI analysis is unavailable.');
        } finally {
            if (mounted.current && my === seq.current.intel) setIntelLoading(false);
        }
    }, []);

    const fetchSuggestions = useCallback(async () => {
        setSuggestionsLoading(true);
        try {
            const data = await alertRuleService.fetchAiSuggestedRules();
            if (mounted.current) setSuggestions(Array.isArray(data) ? data : (data?.data || []));
        } catch (_) {
            if (mounted.current) setSuggestions([]);
        } finally {
            if (mounted.current) setSuggestionsLoading(false);
        }
    }, []);

    useEffect(() => { fetchAlerts(); }, [fetchAlerts]);
    useEffect(() => { fetchRules(); }, [fetchRules]);
    useEffect(() => { fetchIntel(); fetchSuggestions(); }, [fetchIntel, fetchSuggestions]);

    // selection belongs to the visible page
    useEffect(() => { setSelectedIds(new Set()); }, [alertQuery]);

    const refreshAll = useCallback(async ({ refreshAi = false } = {}) => {
        await Promise.all([fetchAlerts({ silent: true }), fetchRules({ silent: true }), fetchIntel({ refresh: refreshAi, silent: true })]);
    }, [fetchAlerts, fetchRules, fetchIntel]);

    // ── live (WebSocket) ─────────────────────────────────────────────────────
    const scheduleLiveRefresh = useCallback(() => {
        if (liveTimer.current) return;
        liveTimer.current = setTimeout(() => {
            liveTimer.current = null;
            fetchAlerts({ silent: true });
            fetchIntel({ silent: true });
        }, LIVE_COALESCE_MS);
    }, [fetchAlerts, fetchIntel]);

    const soundRef = useRef(soundEnabled);
    useEffect(() => { soundRef.current = soundEnabled; }, [soundEnabled]);

    useEffect(() => {
        const onNewAlert = (message) => {
            const p = wsPayload(message);
            const key = p.id ? `a${p.id}` : `${p.sensor_id}|${p.alert_message}`;
            if (!announced.current.has(key)) {
                announced.current.add(key);
                if (announced.current.size > 500) announced.current = new Set([...announced.current].slice(-200));
                const sev = String(p.severity || '').toLowerCase();
                toast?.({
                    title: `New ${sev ? sev.toUpperCase() : ''} alert · ${p.sensor_name || p.sensor_id || 'System'}`,
                    description: p.alert_message || 'An anomaly has been detected.',
                    status: sev === 'critical' ? 'error' : sev === 'high' ? 'warning' : 'info',
                    duration: 7000,
                    isClosable: true,
                    position: 'top-right',
                    variant: 'solid',
                });
                if (soundRef.current && (sev === 'critical' || sev === 'high')) playAlertTone(sev);
                if (p.id) setHighlightIds(prev => new Set([...prev, p.id]));
            }
            scheduleLiveRefresh();
        };
        const onChanged = () => scheduleLiveRefresh();
        const onAiSnapshot = (message) => {
            const p = wsPayload(message);
            if (p?.health) {
                setIntel(prev => (prev ? { ...prev, health: p.health, insights: p.insights?.length ? p.insights : prev.insights, live_at: p.generated_at } : prev));
            }
            fetchIntel({ silent: true });
        };
        const onEscalated = (message) => {
            const p = wsPayload(message);
            toast?.({ title: 'Escalated to supervisors', description: p.title || `Alert #${p.alertId}`, status: 'warning', duration: 9000, isClosable: true, position: 'top-right' });
            scheduleLiveRefresh();
        };

        const subs = [
            ['new_sensor_alert', onNewAlert],
            ['critical_anomaly', onNewAlert],
            ['sensor_alert_updated', onChanged],
            ['sensor_alert_deleted', onChanged],
            ['work_order_status_updated', onChanged],
            ['alert_ai_snapshot', onAiSnapshot],
            ['critical_alert_escalated', onEscalated],
        ];
        subs.forEach(([evt, fn]) => webSocketService.subscribe(evt, fn));
        return () => subs.forEach(([evt, fn]) => webSocketService.unsubscribe(evt, fn));
    }, [scheduleLiveRefresh, fetchIntel, toast]);

    // ── filters ──────────────────────────────────────────────────────────────
    const setAlertFilter = useCallback((name, value) => setAlertFilters(prev => ({ ...prev, [name]: value, page: name === 'page' ? value : 1 })), []);
    const setRuleFilter = useCallback((name, value) => setRuleFilters(prev => ({ ...prev, [name]: value, page: name === 'page' ? value : 1 })), []);
    const resetAlertFilters = useCallback(() => setAlertFilters(prev => ({ ...prev, search: '', status: '', severity: '', page: 1 })), []);

    // ── selection ────────────────────────────────────────────────────────────
    const toggleSelect = useCallback((id) => setSelectedIds(prev => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    }), []);
    const toggleSelectAll = useCallback((ids) => setSelectedIds(prev => (ids.length && ids.every(id => prev.has(id)) ? new Set() : new Set(ids))), []);
    const clearSelection = useCallback(() => setSelectedIds(new Set()), []);
    const highlight = useCallback((ids) => setHighlightIds(new Set(ids || [])), []);

    // ── alert actions ────────────────────────────────────────────────────────
    const changeAlerts = useCallback(async (ids, status, notes) => {
        const list = [...new Set((ids || []).filter(Boolean))];
        if (!list.length) return false;
        setIsWorking(true);
        // optimistic: show the new status straight away
        const before = alerts;
        if (status) setAlerts(prev => prev.map(a => (list.includes(a.id) ? { ...a, status } : a)));
        try {
            if (list.length === 1 && !notes) {
                await activeSensorAlertService.updateActiveAlert(list[0], { status });
            } else {
                let res;
                try {
                    res = await activeSensorAlertService.bulkUpdateActiveAlerts(list, { status, notes: notes || undefined });
                } catch (e) {
                    // older backend without /bulk → one request per alert
                    if (!/404|not found/i.test(e?.message || '')) throw e;
                    await Promise.all(list.map(id => activeSensorAlertService.updateActiveAlert(id, { status, ...(notes ? { notes } : {}) })));
                    res = { updated: list.length, failed: 0 };
                }
                if (res?.failed) toast?.({ title: `${res.updated} updated, ${res.failed} failed`, status: 'warning', duration: 5000, isClosable: true });
            }
            toast?.({ title: `${list.length} alert${list.length > 1 ? 's' : ''} ${statusLabel(status || 'updated')}`, status: 'success', duration: 2500 });
            setSelectedIds(prev => { const n = new Set(prev); list.forEach(id => n.delete(id)); return n; });
            await Promise.all([fetchAlerts({ silent: true }), fetchIntel({ silent: true })]);
            return true;
        } catch (e) {
            setAlerts(before);
            notifyError('Could not update the alert(s)', e);
            return false;
        } finally {
            if (mounted.current) setIsWorking(false);
        }
    }, [alerts, fetchAlerts, fetchIntel, notifyError, toast]);

    const acknowledge = useCallback((ids, notes) => changeAlerts(ids, 'acknowledged', notes), [changeAlerts]);
    const resolve = useCallback((ids, notes) => changeAlerts(ids, 'resolved', notes), [changeAlerts]);

    const openAlertById = useCallback(async (id) => {
        const local = alerts.find(a => a.id === id);
        if (local) return local;
        try {
            const res = await activeSensorAlertService.fetchActiveAlertById(id);
            return res?.data || res;
        } catch (e) {
            notifyError('Could not open the alert', e);
            return null;
        }
    }, [alerts, notifyError]);

    // ── rule actions ─────────────────────────────────────────────────────────
    const saveRule = useCallback(async (ruleId, data) => {
        setIsWorking(true);
        try {
            if (ruleId) {
                await alertRuleService.updateAlertRule(ruleId, data);
                toast?.({ title: 'Alert rule updated', status: 'success', duration: 3000 });
            } else {
                await alertRuleService.createAlertRule(data);
                toast?.({ title: 'Alert rule created', status: 'success', duration: 3000 });
            }
            await Promise.all([fetchRules({ silent: true }), fetchSuggestions(), fetchIntel({ silent: true })]);
            return true;
        } catch (e) {
            notifyError('Error saving alert rule', e);
            return false;
        } finally {
            if (mounted.current) setIsWorking(false);
        }
    }, [fetchRules, fetchSuggestions, fetchIntel, notifyError, toast]);

    const deleteRule = useCallback(async (ruleId) => {
        try {
            await alertRuleService.deleteAlertRule(ruleId);
            toast?.({ title: 'Alert rule deleted', status: 'success', duration: 3000 });
            await Promise.all([fetchRules({ silent: true }), fetchIntel({ silent: true })]);
        } catch (e) { notifyError('Error deleting alert rule', e); }
    }, [fetchRules, fetchIntel, notifyError, toast]);

    const toggleRule = useCallback(async (rule) => {
        const next = !rule.is_enabled;
        setRules(prev => prev.map(r => (r.id === rule.id ? { ...r, is_enabled: next } : r)));
        try {
            await alertRuleService.updateAlertRule(rule.id, { is_enabled: next });
            toast?.({ title: `Rule ${next ? 'enabled' : 'disabled'}`, status: 'success', duration: 2500 });
            fetchIntel({ silent: true });
        } catch (e) {
            setRules(prev => prev.map(r => (r.id === rule.id ? { ...r, is_enabled: !next } : r)));
            notifyError('Error toggling rule', e);
        }
    }, [fetchIntel, notifyError, toast]);

    /** FlowSense AI tuning: raise a flapping rule's duration to the suggested value. */
    const applyRuleTuning = useCallback(async (ruleId, durationMinutes) => {
        try {
            await alertRuleService.updateAlertRule(ruleId, { duration_minutes: durationMinutes });
            toast?.({ title: 'Rule tuned', description: `Duration set to ${durationMinutes} min. Short blips will no longer raise alerts.`, status: 'success', duration: 4000 });
            await Promise.all([fetchRules({ silent: true }), fetchIntel({ refresh: true, silent: true })]);
        } catch (e) { notifyError('Could not tune the rule', e); }
    }, [fetchRules, fetchIntel, notifyError, toast]);

    // ── misc ─────────────────────────────────────────────────────────────────
    const setSoundEnabled = useCallback((on) => {
        setSoundEnabledState(on);
        writeLocal('flowSense_alertSound', on);
        if (on) playAlertTone('high'); // confirms sound works (and unlocks audio)
    }, []);

    const exportAlerts = useCallback((type) => {
        const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
        const triage = intel?.triage_by_id || {};
        if (type === 'json') {
            downloadFile(`flowsense-alerts-${stamp}.json`, JSON.stringify(alerts, null, 2), 'application/json');
        } else {
            const csv = toCsv(
                ['ID', 'Severity', 'Status', 'AI priority', 'Rule', 'Sensor', 'Sensor name', 'Asset', 'Message', 'Value', 'Triggered', 'Acknowledged', 'Resolved', 'Work order'],
                alerts.map(a => [
                    a.id, a.severity, a.status, triage[a.id]?.score ?? '', a.rule_name || '', a.sensor_id || '', a.sensor_name || '',
                    a.asset_location_description || a.asset_id || '', a.alert_message || '', a.last_violation_value ?? '',
                    a.triggered_at ? new Date(a.triggered_at).toLocaleString() : '', a.acknowledged_at ? new Date(a.acknowledged_at).toLocaleString() : '',
                    a.resolved_at ? new Date(a.resolved_at).toLocaleString() : '', a.work_order_id || '',
                ])
            );
            downloadFile(`flowsense-alerts-${stamp}.csv`, csv, 'text/csv;charset=utf-8');
        }
        toast?.({ title: `${type.toUpperCase()} exported (${alerts.length} alerts on this page)`, status: 'success', duration: 2500 });
    }, [alerts, intel, toast]);

    return {
        // data
        alerts, alertPagination, alertFilters, alertsLoading, alertsError, isSearchingAlerts,
        rules, rulePagination, ruleFilters, rulesLoading, isSearchingRules,
        stats, intel, intelLoading, intelError, suggestions, suggestionsLoading,
        selectedIds, highlightIds, soundEnabled, lastUpdated, isWorking,
        // fetch
        fetchAlerts, fetchRules, fetchIntel, fetchStats, fetchSuggestions, refreshAll,
        // filters
        setAlertFilter, setRuleFilter, resetAlertFilters,
        // selection
        toggleSelect, toggleSelectAll, clearSelection, highlight,
        // actions
        acknowledge, resolve, openAlertById, saveRule, deleteRule, toggleRule, applyRuleTuning,
        setSoundEnabled, exportAlerts,
    };
}
// --- END UPDATE
